import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Eye, Trash2, ZoomIn, Edit, X, Check } from "lucide-react";
import { IrisPhoto } from "@/types/iridology";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface IrisPhotoGalleryProps {
  photos: IrisPhoto[];
  onDelete?: (photoId: string) => void;
  onUpdateNotes?: (photoId: string, notes: string) => void;
  readOnly?: boolean;
}

export const IrisPhotoGallery: React.FC<IrisPhotoGalleryProps> = ({
  photos,
  onDelete,
  onUpdateNotes,
  readOnly = false,
}) => {
  const [selectedPhoto, setSelectedPhoto] = useState<IrisPhoto | null>(null);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState("");

  const rightEyePhotos = photos.filter(p => p.eye_side === 'right');
  const leftEyePhotos = photos.filter(p => p.eye_side === 'left');

  const handleZoom = (photo: IrisPhoto) => {
    setSelectedPhoto(photo);
    setNotesValue(photo.notes || "");
    setEditingNotes(false);
  };

  const handleSaveNotes = () => {
    if (selectedPhoto && onUpdateNotes) {
      onUpdateNotes(selectedPhoto.id, notesValue);
      setEditingNotes(false);
    }
  };

  const renderPhotoGroup = (groupPhotos: IrisPhoto[], title: string, side: 'right' | 'left') => {
    if (groupPhotos.length === 0) return null;

    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Eye className="w-4 h-4 text-muted-foreground" />
          <h4 className="font-medium text-sm">{title}</h4>
          <Badge variant="secondary" className="text-xs">{groupPhotos.length}</Badge>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {groupPhotos.map((photo) => (
            <Card key={photo.id} className="group overflow-hidden">
              <CardContent className="p-0 relative">
                <div className="aspect-square relative overflow-hidden">
                  <img
                    src={photo.photo_url}
                    alt={`Iris ${side === 'right' ? 'droit' : 'gauche'}`}
                    className="w-full h-full object-cover transition-transform group-hover:scale-105"
                  />
                  
                  {/* Overlay on hover */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Button
                      size="icon"
                      variant="secondary"
                      onClick={() => handleZoom(photo)}
                      className="w-8 h-8"
                    >
                      <ZoomIn className="w-4 h-4" />
                    </Button>
                    {!readOnly && onDelete && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            size="icon"
                            variant="destructive"
                            className="w-8 h-8"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Supprimer cette photo ?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Cette action est irréversible. La photo sera définitivement supprimée.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annuler</AlertDialogCancel>
                            <AlertDialogAction onClick={() => onDelete(photo.id)}>
                              Supprimer
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </div>
                
                {/* Notes indicator */}
                {photo.notes && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                    <p className="text-white text-xs truncate">{photo.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  };

  if (photos.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Eye className="w-12 h-12 mx-auto mb-3 opacity-30" />
        <p className="text-sm">Aucune photo d'iris pour cette séance</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {renderPhotoGroup(rightEyePhotos, "Œil droit", "right")}
      {renderPhotoGroup(leftEyePhotos, "Œil gauche", "left")}

      {/* Fullscreen Dialog */}
      <Dialog open={!!selectedPhoto} onOpenChange={(open) => !open && setSelectedPhoto(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              {selectedPhoto?.eye_side === 'right' ? 'Œil droit' : 'Œil gauche'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedPhoto && (
            <div className="space-y-4">
              {/* Image */}
              <div className="relative rounded-lg overflow-hidden bg-black">
                <img
                  src={selectedPhoto.photo_url}
                  alt="Iris"
                  className="w-full h-auto max-h-[60vh] object-contain"
                />
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Notes</label>
                  {!readOnly && !editingNotes && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditingNotes(true)}
                      className="gap-1"
                    >
                      <Edit className="w-3 h-3" />
                      Modifier
                    </Button>
                  )}
                </div>
                
                {editingNotes ? (
                  <div className="space-y-2">
                    <Textarea
                      value={notesValue}
                      onChange={(e) => setNotesValue(e.target.value)}
                      placeholder="Ajouter des notes pour cette photo..."
                      rows={3}
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingNotes(false);
                          setNotesValue(selectedPhoto.notes || "");
                        }}
                        className="gap-1"
                      >
                        <X className="w-3 h-3" />
                        Annuler
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSaveNotes}
                        className="gap-1"
                      >
                        <Check className="w-3 h-3" />
                        Enregistrer
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground bg-muted p-3 rounded-lg">
                    {selectedPhoto.notes || "Aucune note"}
                  </p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
